import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { c as cl } from './clsx.m.js';
import { c as createTypeValidator, a as createLogger, e as emitEvent, l as logDeprecatedEventOnce } from './utils.js';

// Constants
const component = 'cl-lightbox';
const lightboxConstants = {
  ALIGNMENT: {
    TOP_LEFT: 'top-left',
    TOP_CENTER: 'top-center',
    TOP_RIGHT: 'top-right',
    CENTER_LEFT: 'center-left',
    CENTER: 'center',
    CENTER_RIGHT: 'center-right',
    BOTTOM_LEFT: 'bottom-left',
    BOTTOM_CENTER: 'bottom-center',
    BOTTOM_RIGHT: 'bottom-right',
  },
  ALIGNMENT_MAP: {
    top: 'flex-start',
    left: 'flex-start',
    center: 'center',
    bottom: 'flex-end',
    right: 'flex-end',
  },
};
// Type-validators
const lightboxValidator = {
  alignment: createTypeValidator(lightboxConstants.ALIGNMENT),
};

const clLightboxCss = ".sc-cl-lightbox-h{--lightbox-animation-close:var(--animation-duration-normal) cubic-bezier(0, 0, 0.2, 1);--lightbox-animation-open:var(--animation-duration-normal) cubic-bezier(0, 0, 0.2, 1);--lightbox-blur:var(--size-2);--lightbox-z-index:var(--z-index-10)}@keyframes fade-out{from{opacity:var(--opacity-5)}to{opacity:var(--opacity-0)}}@keyframes fade-in{from{opacity:var(--opacity-0)}to{opacity:var(--opacity-5)}}.lightbox.sc-cl-lightbox-h{display:block;height:100vh;inset:0;overflow:hidden;position:fixed;-webkit-user-select:none;-moz-user-select:none;user-select:none;visibility:hidden;width:100vw;z-index:var(--lightbox-z-index)}.lightbox.sc-cl-lightbox-h .lightbox__background.sc-cl-lightbox{background-color:rgba(var(--color-neutral-900), var(--opacity-2));display:block;height:100%;inset:0;overflow:hidden;position:absolute;-webkit-user-select:none;-moz-user-select:none;user-select:none;width:100%;z-index:var(--z-index-0)}.lightbox.sc-cl-lightbox-h .lightbox__content.sc-cl-lightbox{align-items:var(--lightbox-align-items, center);display:flex;height:100%;inset:0;justify-content:var(--lightbox-justify-content, center);position:relative;width:100%;z-index:var(--z-index-1)}.lightbox--blurred.sc-cl-lightbox-h{-webkit-backdrop-filter:blur(var(--lightbox-blur));backdrop-filter:blur(var(--lightbox-blur))}.sc-cl-lightbox-h.lightbox--close-on-click.sc-cl-lightbox-s>*{cursor:auto}.lightbox--transparent.sc-cl-lightbox-h .lightbox__background.sc-cl-lightbox{background-color:transparent}.lightbox--opening.sc-cl-lightbox-h{visibility:visible}.lightbox--opening.sc-cl-lightbox-h .lightbox__background.sc-cl-lightbox{animation:fade-in var(--lightbox-animation-open);opacity:var(--opacity-0)}.lightbox--opened.sc-cl-lightbox-h{visibility:visible}.lightbox--opened.sc-cl-lightbox-h .lightbox__background.sc-cl-lightbox{opacity:var(--opacity-5)}.lightbox--closing.sc-cl-lightbox-h{visibility:visible}.lightbox--closing.sc-cl-lightbox-h .lightbox__background.sc-cl-lightbox{animation:fade-out var(--lightbox-animation-close);opacity:var(--opacity-5)}.lightbox--closed.sc-cl-lightbox-h{visibility:hidden}.lightbox--closed.sc-cl-lightbox-h .lightbox__background.sc-cl-lightbox{opacity:var(--opacity-0)}";

const ClLightbox = /*@__PURE__*/ proxyCustomElement(class ClLightbox extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.clCloseEnd = createEvent(this, "clCloseEnd", 7);
    this.clCloseStart = createEvent(this, "clCloseStart", 7);
    this.clOpenEnd = createEvent(this, "clOpenEnd", 7);
    this.clOpenStart = createEvent(this, "clOpenStart", 7);
    this.closeEnd = createEvent(this, "closeEnd", 7);
    this.closeStart = createEvent(this, "closeStart", 7);
    this.openEnd = createEvent(this, "openEnd", 7);
    this.openStart = createEvent(this, "openStart", 7);
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    /**
     * Function that emits the correct animation event based on whether the dropdown is opening or closing
     */
    this.handleAnimationEvent = (event) => {
      const { animationName, type } = event;
      const isOpenEvent = animationName === 'fade-in';
      const isCloseEvent = animationName === 'fade-out';
      switch (true) {
        case isOpenEvent && type === 'animationstart':
          emitEvent(this, 'clOpenStart', { component, originalEvent: event }, null);
          break;
        case isOpenEvent && type === 'animationend':
          this.element.classList.remove('lightbox--opening');
          this.element.classList.add('lightbox--opened');
          emitEvent(this, 'clOpenEnd', { component, originalEvent: event }, null);
          break;
        case isCloseEvent && type === 'animationstart':
          emitEvent(this, 'clCloseStart', { component, originalEvent: event }, null);
          break;
        case isCloseEvent && type === 'animationend':
          this.element.classList.remove('lightbox--closing');
          this.element.classList.add('lightbox--closed');
          emitEvent(this, 'clCloseEnd', { component, originalEvent: event }, null);
          break;
      }
    };
    this.align = 'center';
    this.blurred = false;
    this.closeOnClick = false;
    this.open = false;
    this.transparent = false;
  }
  /**
   * Validates the align property and updates the styles accordingly
   */
  handleAlign(align) {
    if (!lightboxValidator.alignment(align)) {
      this.logger.error.attribute.align(`Given invalid value "${align}".`, `Valid options are: ${Object.values(lightboxConstants.ALIGNMENT).join(', ')}.`);
      this.align = 'center';
      return;
    }
    let justifyContent;
    let alignItems;
    if (align === lightboxConstants.ALIGNMENT.CENTER) {
      justifyContent = lightboxConstants.ALIGNMENT_MAP.center;
      alignItems = lightboxConstants.ALIGNMENT_MAP.center;
    }
    else {
      const [y, x] = align.split('-');
      alignItems = lightboxConstants.ALIGNMENT_MAP[y];
      justifyContent = lightboxConstants.ALIGNMENT_MAP[x];
    }
    this.element.style.setProperty('--lightbox-justify-content', justifyContent);
    this.element.style.setProperty('--lightbox-align-items', alignItems);
  }
  /**
   * Updates the lightbox element styles based on the `open` property
   */
  handleOpen(isOpen, wasOpen) {
    switch (true) {
      case isOpen && !wasOpen: {
        this.element.classList.remove('lightbox--closing');
        this.element.classList.remove('lightbox--closed');
        this.element.classList.add('lightbox--opening');
        break;
      }
      case !isOpen && wasOpen: {
        this.element.classList.remove('lightbox--opening');
        this.element.classList.remove('lightbox--opened');
        this.element.classList.add('lightbox--closing');
        break;
      }
      case !isOpen && !wasOpen: {
        this.element.classList.remove('lightbox--opened');
        this.element.classList.add('lightbox--closed');
        break;
      }
    }
  }
  /**
   * Every time the component is connected to the DOM:
   * - Console log deprecation warnings;
   * - Validates `align` property
   * - Open the lightbox if the `open` property is true
   */
  connectedCallback() {
    logDeprecatedEventOnce(this.logger, 'closeEnd', 'clCloseEnd');
    logDeprecatedEventOnce(this.logger, 'closeStart', 'clCloseStart');
    logDeprecatedEventOnce(this.logger, 'openEnd', 'clOpenEnd');
    logDeprecatedEventOnce(this.logger, 'openStart', 'clOpenStart');
    this.handleAlign(this.align);
    this.handleOpen(this.open);
  }
  /**
   * Closes the lightbox if the click was performed on the lightbox and if the closeOnClick property is true.
   */
  handleClick(event) {
    if (this.closeOnClick && event.target === this.element.children[1]) {
      this.open = false;
    }
  }
  render() {
    const { blurred, closeOnClick, transparent } = this;
    return (h(Host, { class: cl('lightbox', {
        'lightbox--blurred': blurred,
        'lightbox--close-on-click': closeOnClick,
        'lightbox--transparent': transparent,
      }), onAnimationEnd: this.handleAnimationEvent, onAnimationStart: this.handleAnimationEvent }, h("div", { class: 'lightbox__background' }), h("div", { class: 'lightbox__content' }, h("slot", null))));
  }
  get element() { return this; }
  static get watchers() { return {
    "align": ["handleAlign"],
    "open": ["handleOpen"]
  }; }
  static get style() { return clLightboxCss; }
}, [6, "cl-lightbox", {
    "align": [1025],
    "blurred": [4],
    "closeOnClick": [4, "close-on-click"],
    "open": [1540],
    "transparent": [4]
  }, [[2, "click", "handleClick"]]]);
function defineCustomElement() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-lightbox"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-lightbox":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClLightbox);
      }
      break;
  } });
}

export { ClLightbox as C, defineCustomElement as d };

//# sourceMappingURL=cl-lightbox2.js.map